import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Alert, Modal, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Barcode from 'react-barcode';
import JsBarcode from 'jsbarcode';
import { materialsAPI } from '../utils/api';

const MaterialList = () => {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Load materials function
  const loadMaterials = useCallback(async (search = '') => {
    try {
      setLoading(true);
      const params = search ? { search } : {};
      const res = await materialsAPI.getAll(params);
      setMaterials(res.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  }, []);

  // Throttled search effect (only after initial mount)
  useEffect(() => {

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for search (500ms delay)
    searchTimeoutRef.current = setTimeout(() => {
      loadMaterials(searchQuery);
    }, 500);

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, loadMaterials]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`คุณแน่ใจหรือไม่ที่จะลบวัตถุดิบ "${name}"?`)) {
      return;
    }

    try {
      await materialsAPI.delete(id);
      alert('ลบวัตถุดิบสำเร็จ');
      loadMaterials(searchQuery);
    } catch (err) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบวัตถุดิบ');
    }
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) {
      alert('กรุณาเลือกไฟล์');
      return;
    }

    if (importFile.size > MAX_FILE_SIZE) {
      alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
      return;
    }

    setImporting(true);
    try {
      const res = await materialsAPI.import(importFile);
      alert(res.data.message);
      setShowImportModal(false);
      setImportFile(null);
      loadMaterials();
    } catch (err) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการนำเข้าข้อมูล');
    } finally {
      setImporting(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const res = await materialsAPI.export();
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'materials.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการดาวน์โหลด Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadBarcode = (material) => {
    if (!material.barcode) {
      alert('สินค้านี้ไม่มีบาร์โค้ด');
      return;
    }

    try {
      // Create a temporary canvas for barcode
      const barcodeCanvas = document.createElement('canvas');

      // Generate barcode on canvas
      JsBarcode(barcodeCanvas, material.barcode, {
        format: 'CODE128',
        width: 3,
        height: 100,
        displayValue: true,
        fontSize: 20,
        margin: 10
      });

      // Create final canvas with extra space for material name
      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');

      // Set canvas size (barcode height + space for name)
      finalCanvas.width = barcodeCanvas.width;
      finalCanvas.height = barcodeCanvas.height + 40;

      // Fill white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

      // Draw barcode
      ctx.drawImage(barcodeCanvas, 0, 0);

      // Draw material name below barcode
      ctx.fillStyle = 'black';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(material.name, finalCanvas.width / 2, barcodeCanvas.height + 25);

      // Convert canvas to blob and download
      finalCanvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `barcode-${material.code}-${material.barcode}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    } catch (err) {
      alert('เกิดข้อผิดพลาดในการดาวน์โหลดบาร์โค้ด');
      console.error('Barcode download error:', err);
    }
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-box me-2"></i>
            จัดการวัตถุดิบ
          </h2>
          <p className="text-muted">จัดการข้อมูลวัตถุดิบทั้งหมด</p>
        </Col>
        <Col xs="auto">
          <Button variant="success" className="me-2" onClick={() => setShowImportModal(true)}>
            <i className="bi bi-file-earmark-excel me-2"></i>
            นำเข้า Excel
          </Button>
          <Button
            variant="outline-success"
            className="me-2"
            onClick={handleExportExcel}
            disabled={exporting}
          >
            <i className="bi bi-file-earmark-arrow-down me-2"></i>
            {exporting ? 'กำลังดาวน์โหลด...' : 'ดาวน์โหลด Excel'}
          </Button>
          <Link to="/materials/create">
            <Button variant="primary">
              <i className="bi bi-plus-circle me-2"></i>
              เพิ่มวัตถุดิบ
            </Button>
          </Link>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Header>
          <Row>
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="ค้นหาด้วย รหัส, ชื่อ หรือ บาร์โค้ด..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <Form.Text className="text-muted">
                ผลการค้นหาจะอัพเดทอัตโนมัติ
              </Form.Text>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : materials.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
              <p className="text-muted mt-3">ไม่พบวัตถุดิบ</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>รหัส</th>
                  <th>ชื่อ</th>
                  <th>บาร์โค้ด</th>
                  <th>สต็อกคงเหลือ</th>
                  <th>จองแล้ว</th>
                  <th>พร้อมใช้</th>
                  <th>ราคาสำหรับสาขา</th>
                  <th>หน่วย</th>
                  <th>หมวดหมู่</th>
                  <th>การจัดการ</th>
                </tr>
              </thead>
              <tbody>
                {materials.map(material => (
                  <tr key={material.id}>
                    <td><strong>{material.code}</strong></td>
                    <td>{material.name}</td>
                    <td>
                      {material.barcode ? (
                        <div
                          onClick={() => handleDownloadBarcode(material)}
                          style={{ cursor: 'pointer' }}
                          title="คลิกเพื่อดาวน์โหลดบาร์โค้ด"
                        >
                          <Barcode
                            value={material.barcode}
                            width={1}
                            height={40}
                            fontSize={12}
                            margin={0}
                          />
                        </div>
                      ) : (
                        <small className="text-muted">-</small>
                      )}
                    </td>
                    <td>
                      {material.isOnDemand ? (
                        <Badge bg="secondary">สั่งตามต้องการ</Badge>
                      ) : (
                        <Badge bg={material.quantityOnHand > 0 ? 'success' : 'danger'}>
                          {material.quantityOnHand || 0}
                        </Badge>
                      )}
                    </td>
                    <td>
                      {material.isOnDemand ? (
                        <span className="text-muted">-</span>
                      ) : (
                        <Badge bg="warning" text="dark">
                          {material.quantityReserved || 0}
                        </Badge>
                      )}
                    </td>
                    <td>
                      {material.isOnDemand ? (
                        <span className="text-muted">-</span>
                      ) : (
                        <Badge bg="info">
                          {((material.quantityOnHand || 0) - (material.quantityReserved || 0))}
                        </Badge>
                      )}
                    </td>
                    <td>{material.salePrice.toFixed(2)} ฿</td>
                    <td>{material.unit}</td>
                    <td>{material.category || '-'}</td>
                    <td>
                      <Link to={`/materials/${material.id}/edit`}>
                        <Button variant="outline-primary" size="sm" className="me-1">
                          <i className="bi bi-pencil"></i>
                        </Button>
                      </Link>
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-1"
                        title="ประวัติราคา"
                        onClick={() => navigate(`/materials/${material.id}/price-history`)}
                      >
                        <i className="bi bi-graph-up"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(material.id, material.name)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Import Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>นำเข้าข้อมูลจาก Excel</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleImport}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>เลือกไฟล์ Excel</Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files[0])}
                required
              />
              <Form.Text className="text-muted">
                ไฟล์ต้องมีคอลัมน์: รหัสสินค้า, ชื่อสินค้า, Barcode, ต้นทุน, หน่วยนับ, etc.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowImportModal(false)}>
              ยกเลิก
            </Button>
            <Button variant="primary" type="submit" disabled={importing}>
              {importing ? 'กำลังนำเข้า...' : 'นำเข้าข้อมูล'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default MaterialList;
